import { ImminentRiskEvents } from '../../../src/models/imminent-risk-model-impl'
import { NotificationTypes, VoyNotification } from '../../../src/services/types/vessel-notifications-services.types'
import { NotificationTypesExtended, activityLogNotification } from '../../../src/models/types/vessel-notifications.types'
import { GetFormattedDayMonth, GetUTCTimestamp, prettyTimelapsed } from '../../../src/utils/helpers'
import { SvgIcons } from '../../voyage-svg-icons/src/voyage-svg-icons'

export type NoticiationIcon = {
  name?: string
  customName?: keyof typeof SvgIcons
  bgColor: string
  color?: string
  size?: string
}

const IconType = {
  risk_heavyWeather: { customName: 'customHeavyweather', bgColor: '#ff5f00', color: '#ffffff' },
  risk_parametricRoll: { customName: 'customParametricroll', bgColor: '#f159d3', color: '#ffffff' },
  risk_heavyRolling: { customName: 'customParametricroll', bgColor: '#a880f5', color: '#ffffff' },
  risk_highAccelerationForce: { customName: 'customHighAccelerationForce', bgColor: '#BF6410', color: '#ffffff' },
} as const

export interface ActionButton {
  label: string
  action: string
  data?: Record<string, any>
}

type WeatherRiskType =
  | NotificationTypesExtended.HeavyWeather
  | NotificationTypesExtended.ParametricRoll
  | NotificationTypesExtended.HeavyRolling
  | NotificationTypesExtended.HighAccelerationForce

const createWeatherRiskItem = (
  notification: activityLogNotification,
  type: WeatherRiskType,
): ActivityLogItem | null => {
  const data = notification.details
  if (!data?.start_time) return null

  const date = GetFormattedDayMonth(data.start_time)
  const time = GetUTCTimestamp(data.start_time)
  const definitions = {
    [NotificationTypesExtended.HeavyWeather]: {
      title: 'Heavy weather risk',
      description: `Risk of Heavy weather foreseen on ${date} at ${time}.`,
      icon: IconType.risk_heavyWeather,
    },
    [NotificationTypesExtended.ParametricRoll]: {
      title: 'Parametric roll risk',
      description: `Risk of Parametric roll foreseen on ${date} at ${time}.`,
      icon: IconType.risk_parametricRoll,
    },
    [NotificationTypesExtended.HeavyRolling]: {
      title: 'Heavy rolling risk',
      description: `Risk of Heavy rolling foreseen on ${date} at ${time}.`,
      icon: IconType.risk_heavyRolling,
    },
    [NotificationTypesExtended.HighAccelerationForce]: {
      title: 'High acceleration force risk',
      description: `Risk of High acceleration force foreseen on ${date} at ${time}.`,
      icon: IconType.risk_highAccelerationForce,
    },
  }
  const definition = definitions[type]

  return {
    id: notification.id,
    type,
    title: definition.title,
    description: definition.description,
    icon: definition.icon,
    timestamp: prettyTimelapsed(notification.created_at),
  }
}

export const createActivityLogItems = (notifications: UnifiedNotification[]): ActivityLogItem[] => {
  const activityLog: ActivityLogItem[] = []

  notifications.forEach((notification) => {
    switch (notification.type) {
      case NotificationTypes.ImminentRisk: {
        const timestamp = new Date().toISOString()
        activityLog.push({
          id: notification.id,
          type: notification.type,
          title: `High roll angle risk projected within the next 30 minutes | ${new Date(timestamp).toUTCString().slice(5, -7)} UTC`,
          description: 'Your vessel is currently at risk of Parametric and Synchronous Roll. Please view the Polar Chart advice to take an immediate action.',
          icon: SvgIcons.polarChartIndicator,
          bgColor: '#e6f1fb',
          color: '#001b44',
          actions: [{ label: 'View Polar Chart Advice', action: ImminentRiskEvents.hasImminentRisk }],
          timestamp: 'Just now',
        })
        break
      }
      case NotificationTypesExtended.HeavyWeather:
      case NotificationTypesExtended.ParametricRoll:
      case NotificationTypesExtended.HeavyRolling:
      case NotificationTypesExtended.HighAccelerationForce: {
        const item = createWeatherRiskItem(notification as activityLogNotification, notification.type)
        if (item) activityLog.push(item)
        break
      }
    }
  })

  return activityLog
}

export interface ActivityLogItem {
  id: string
  type?: string
  title: string
  description?: string
  icon?: any
  timestamp?: string
  actions?: ActionButton[]
  bgColor?: string
  color?: string
}

export type UnifiedNotification =
  | VoyNotification<NotificationTypes.Alarm>
  | VoyNotification<NotificationTypes.EtaDeviation>
  | VoyNotification<NotificationTypes.NPS>
  | VoyNotification<NotificationTypes.HeavyWeather>
  | VoyNotification<NotificationTypes.ParametricRoll>
  | VoyNotification<NotificationTypes.HeavyRolling>
  | VoyNotification<NotificationTypes.HighAccelerationForce>
  | VoyNotification<NotificationTypes.ETAAlerts>
  | VoyNotification<NotificationTypes.NoPlanPreEtd>
  | VoyNotification<NotificationTypes.ImminentRisk>
  | activityLogNotification
